// Page docs (structure + permissions + flows): pages/regulations/README.md
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useRouter } from "next/router";
import NavBar from "@/components/Importable/Navigation";
import SubscriptionBanner from "@/components/Banners/SubscriptionBanner";
import SectionFooter from "@/components/CreateForm/Footer";
import Sidenav from "@/components/Sidenav";
import { featuredRegulations } from "@/data/regulationsData";
import { getAllNewlyAdded, getIndexCache, setIndexCache } from "@/utils/localStore";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useDataVersionCheck } from "@/hooks/useDataVersionCheck";
import { blue } from "@/theme/colors";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { openAccessGateModal } from "@/utils/accessGate";
import { api, useGetRegulationsQuery } from "@/src/services/api";
import { useAppDispatch } from "@/src/store/hooks";
import { isLocalDev, apiRegulationToCard } from "@/utils/apiMappers";
import { useProgressiveLoading } from "@/hooks/useProgressiveLoading";
import RegulationCardSkeleton from "@/components/Skeleton/RegulationCardSkeleton";
import { RegulationCard } from "@/components/Importable/Cards";
import { IndexPageHead, IndexLayoutShell, IndexListHeader, IndexResultsGrid, useRelevanceSplit, CreateDraftCard } from "@/components/Importable/articles-and-regulations";
import FilterBar from "@/components/OrganizationSections/FilterBar";
import { useIndexFiltering, INDEX_FILTER_MIN } from "@/hooks/useIndexFiltering";
import { useIndexSideNav } from "@/hooks/useIndexSideNav";
import { useIndexLayout } from "@/hooks/useIndexLayout";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ACCENT = {
  primary: blue.primary,
  dark: blue.dark
};
const normalizeTagList = values => {
  if (!Array.isArray(values) || !values.length) return [];
  return values.map(value => (value || "").trim()).filter(value => Boolean(value));
};
const buildNavSortBucket = values => {
  if (!values.length) return "~";
  return [...values].map(value => value.toLowerCase()).sort().join("|");
};
const sortRegulationNavEntries = entries => {
  return [...entries].sort((a, b) => {
    const regionCompare = buildNavSortBucket(a.regionTags).localeCompare(buildNavSortBucket(b.regionTags));
    if (regionCompare !== 0) return regionCompare;
    const filterCompare = buildNavSortBucket(a.filterTags).localeCompare(buildNavSortBucket(b.filterTags));
    if (filterCompare !== 0) return filterCompare;
    return (a.title || "").localeCompare(b.title || "");
  });
};
const publishedCardToNavEntry = regulation => {
  const id = String(regulation.id ?? "").trim();
  if (!id) return null;
  return {
    id,
    title: regulation.title || "Untitled Regulation",
    shortTitle: regulation.shortTitle,
    filterTags: normalizeTagList(regulation.filterTags ?? regulation.tags ?? []),
    regionTags: normalizeTagList(regulation.regionTags ?? regulation.regions ?? [])
  };
};

// Sort by filter chip values (tags), then title.
const tagKey = tags => Array.isArray(tags) && tags.length ? [...tags].map(t => (t || "").toLowerCase()).sort().join("|") : "~";
const compareByChips = (a, b) => {
  const ak = tagKey(a.sortTags || a.tags);
  const bk = tagKey(b.sortTags || b.tags);
  const cmp = ak.localeCompare(bk);
  if (cmp !== 0) return cmp;
  return (a.title ?? "").localeCompare(b.title ?? "");
};

/**
 * Data layer for the regulations index: SSR-safe cache hydration, the live API
 * query (skipped while a fresh cache exists), a reference-preserving smart
 * merge (so React.memo(RegulationCard) can skip unchanged cards), and the
 * local-dev fallback. Hydration ordering here is intentionally delicate.
 */
function useRegulationsIndexData() {
  const dispatch = useAppDispatch();
  const cacheStale = useDataVersionCheck("regulations",
  // Un-skipping alone serves any pre-approval result already in the
  // Redux cache (e.g. loaded by a detail sidenav) without a network
  // hit; invalidating the tag forces a real refetch — and covers
  // consecutive approvals while the page stays mounted.
  useCallback(() => {
    dispatch(api.util.invalidateTags(["Regulations"]));
  }, [dispatch]));
  const [cachedRegs, setCachedRegs] = useState(null);
  // Always start empty so the first render matches server-rendered HTML.
  const [publishedRegs, setPublishedRegs] = useState([]);
  // Suppresses skeleton flash until the first cache read completes.
  const [cacheInitialized, setCacheInitialized] = useState(false);

  // Hydrate from index cache after mount (SSR-safe), with a macrotask retry
  // for localStore's hydration flag.
  useEffect(() => {
    const cFast = getIndexCache("regulations");
    if (cFast?.length) {
      setCachedRegs(cFast);
      setPublishedRegs(cFast);
      setCacheInitialized(true);
      return;
    }
    const t = setTimeout(() => {
      const c = getIndexCache("regulations");
      if (c?.length) {
        setCachedRegs(c);
        setPublishedRegs(prev => c.length >= prev.length ? c : prev);
      }
      setCacheInitialized(true);
    }, 0);
    return () => clearTimeout(t);
  }, []);
  const {
    data: apiRegulations,
    isError: regulationsError
  } = useGetRegulationsQuery(undefined, {
    skip: (cachedRegs?.length ?? 0) > 0 && !cacheStale
  });
  useEffect(() => {
    if (!apiRegulations) return;
    const cards = apiRegulations.filter(r => r.isActive && r.approvalStatus === "approved").map(reg => apiRegulationToCard(reg));
    const seen = new Set();
    const deduped = cards.filter(c => {
      const key = String(c.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // Smart-merge: preserve object references for unchanged items so
    // React.memo(RegulationCard) can skip re-renders; only changed items
    // get a new reference.
    setPublishedRegs(prev => {
      const prevMap = new Map(prev.map(r => [String(r.id), r]));
      let anyChanged = prev.length !== deduped.length;
      const merged = deduped.map(r => {
        const existing = prevMap.get(String(r.id));
        if (existing && existing.title === r.title && existing.lastUpdated === r.lastUpdated && existing.executiveSummary === r.executiveSummary && existing.isNew === r.isNew && existing.publishedAt === r.publishedAt) {
          return existing;
        }
        anyChanged = true;
        return r;
      });
      return anyChanged ? merged : prev;
    });
    setIndexCache("regulations", deduped);
  }, [apiRegulations]);

  // Local-dev fallback: surface admin-approved regulations from localStorage
  // when there's no cache and the API hasn't returned. Guard order matters.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isLocalDev) return;
    if (cachedRegs?.length) return;
    if (apiRegulations) return;
    if (publishedRegs.length > 0) return;
    try {
      const approvals = getAllNewlyAdded("regulation").filter(a => a.operation !== "deactivate");
      const normalized = approvals.map(a => {
        const p = a.payload || {};
        const routeId = p.slug || p.id || String(a.approvalTimestamp);
        return {
          id: routeId,
          approvedId: a.id,
          useAdminPreview: true,
          title: p.title,
          shortTitle: p.shortTitle,
          priority: p.priority,
          tags: [...(p.regionTags || []), ...(p.filterTags || []), ...(p.miscTags || [])],
          regions: p.regions || p.regionTags || [],
          regionTags: p.regionTags || p.regions || [],
          filterTags: p.filterTags || [],
          miscTags: p.miscTags || [],
          sortTags: [...(p.filterTags || [])],
          regulationType: p.type === "article" ? "ARTICLE" : "REGULATORY BRIEF",
          publishedAt: a.approvalTimestamp || Date.now(),
          briefBy: p.author?.name || "Unknown",
          executiveSummary: p.executiveSummary,
          content: p.content,
          isNew: !a.isEdit,
          approvalTimestamp: a.approvalTimestamp
        };
      });
      if (normalized.length) {
        setPublishedRegs(normalized);
      }
    } catch {
      // ignore — fallback only
    }
  }, [cachedRegs, apiRegulations, publishedRegs.length]);
  return {
    publishedRegs,
    regulationsError,
    cacheInitialized
  };
}
const RegulationsPage = () => {
  const theme = useTheme();
  const router = useRouter();
  const {
    settings: siteSettings
  } = useSiteSettings();
  useEffect(() => {
    if (!siteSettings.regulationsVisible) void router.replace("/organizations");
  }, [siteSettings.regulationsVisible, router]);
  const {
    isAuthenticated
  } = useAuth();
  const belowLg = useMediaQuery(theme.breakpoints.down("lg"));
  const {
    publishedRegs,
    regulationsError,
    cacheInitialized
  } = useRegulationsIndexData();
  const {
    items: savedRegulations
  } = useSavedItems("regulation");
  const handleCreateClick = useCallback(() => {
    if (!isAuthenticated) {
      openAccessGateModal({
        reason: "login-required",
        returnUrl: "/regulations/create"
      });
      return;
    }
    router.push("/regulations/create");
  }, [isAuthenticated, router]);

  // ── Sidenav (Saved + All Regulations) ──
  const savedRegNavItems = useMemo(() => {
    if (!savedRegulations.length) return [];
    const seen = new Set();
    const items = [];
    savedRegulations.forEach(item => {
      const key = String(item.resourceSlug ?? item.resourceId ?? "").trim();
      if (!key) return;
      const normalized = key.toLowerCase();
      if (seen.has(normalized)) return;
      seen.add(normalized);
      items.push({
        id: `saved-${normalized}`,
        label: item.resourceTitle || item.resourceSlug || item.resourceId || "Saved Regulation",
        href: `/regulations/${key}`
      });
    });
    return items;
  }, [savedRegulations]);
  const allRegNav = useMemo(() => {
    const map = new Map();
    publishedRegs.forEach(reg => {
      const navEntry = publishedCardToNavEntry(reg);
      if (navEntry) map.set(navEntry.id, navEntry);
    });
    return sortRegulationNavEntries(Array.from(map.values()));
  }, [publishedRegs]);
  const sideItems = useMemo(() => {
    const savedSections = savedRegNavItems.length ? [{
      title: "Saved",
      items: savedRegNavItems
    }] : [];
    return [...savedSections, {
      title: "All Regulations",
      items: allRegNav.map(entry => ({
        id: `all-${entry.id}`,
        label: entry.shortTitle || entry.title,
        href: `/regulations/${entry.id}`
      }))
    }];
  }, [allRegNav, savedRegNavItems]);

  // ── Sidebar layout state + auto-collapse ──
  const [sidebarState, setSidebarState] = useState({
    isOpen: false,
    isPermanent: false,
    sidebarWidth: 320
  });
  const handleSidebarStateChange = useCallback(state => setSidebarState(state), []);
  // Auto-collapse the sidenav on resize below lg if open but not permanent.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      const {
        isOpen,
        isPermanent
      } = sidebarState;
      if (belowLg && isOpen && !isPermanent) {
        window.dispatchEvent(new CustomEvent("sidebarToggle"));
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [belowLg, sidebarState]);

  // ── Local draft ──
  const [draftReg, setDraftReg] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("regulationDraft");
      setDraftReg(raw ? JSON.parse(raw) : null);
    } catch {
      setDraftReg(null);
    }
  }, []);
  const clearDraft = useCallback(() => {
    if (typeof window === "undefined") return;
    localStorage.removeItem("regulationDraft");
    setDraftReg(null);
  }, []);

  // ── Sorted list + progressive loading + relevance split ──
  const sortedPublished = useMemo(() => {
    const seen = new Set();
    const deduped = publishedRegs.filter(r => {
      const key = String(r.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return deduped.sort(compareByChips);
  }, [publishedRegs]);

  // Dynamic filters derived from the loaded regulations — a select only
  // offers values that exist in the data.
  const regFilterSpecs = useMemo(() => [{
    name: "Category",
    getValues: r => [r.category ?? ""]
  }, {
    name: "Region",
    getValues: r => [...(r.regions ?? []), ...(r.regionTags ?? [])]
  }, {
    name: "Type",
    getValues: r => [r.regulationType ?? ""]
  }], []);
  const searchableRegText = useCallback(r => [r.title, r.shortTitle ?? "", r.description ?? "", r.executiveSummary ?? "", (r.tags ?? []).join(" "), r.category ?? ""], []);
  const {
    searchQuery: regSearch,
    setSearchQuery: setRegSearch,
    viewMode: regViewMode,
    setViewMode: setRegViewMode,
    options: regFilterOptions,
    selected: regFilterValues,
    setSelectedAt: setRegFilterAt,
    filtered: filteredRegulations
  } = useIndexFiltering(sortedPublished, regFilterSpecs, searchableRegText, "list", INDEX_FILTER_MIN);
  const {
    visibleItems: visiblePublished,
    showSkeletons,
    skeletonCount
  } = useProgressiveLoading(filteredRegulations, 10, regulationsError, !cacheInitialized);
  const {
    relevant,
    remaining,
    showRelevanceSection,
    getHighlightedTags
  } = useRelevanceSplit(visiblePublished);
  const renderRegulationCard = useCallback(regulation => /*#__PURE__*/_jsx(RegulationCard, {
    regulation: regulation,
    onClick: () => router.push(`/regulations/${regulation.id}`),
    highlightedTags: getHighlightedTags(regulation.tags || []),
    hideTypeChip: true,
    listMode: regViewMode === "list"
  }), [router, getHighlightedTags, regViewMode]);

  // One source of truth — shared with NavBar so the navbar's shift
  // agrees with whether the nav actually renders. Resolves false until
  // the setting is known, so it never flashes in and out.
  const showSideNav = useIndexSideNav("regulations");
  const indexLayout = useIndexLayout("regulations");
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(IndexPageHead, {
      title: "Regulations Overview | Your Company",
      description: "Explore comprehensive regulations and compliance guidelines. Stay informed about the latest regulatory updates and requirements.",
      keywords: "regulations, compliance, legal, guidelines, requirements",
      canonicalUrl: "https://yourdomain.com/regulations",
      twitterDescription: "Explore comprehensive regulations and compliance guidelines.",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Regulations Overview",
        description: "Explore comprehensive regulations and compliance guidelines",
        url: "https://yourdomain.com/regulations"
      }
    }), /*#__PURE__*/_jsx(NavBar, {}), showSideNav && /*#__PURE__*/_jsx(Sidenav, {
      "aria-label": "Regulations Sidebar",
      "data-select-target": "Side Navigation",
      title: "Regulations Index",
      sections: sideItems,
      onSidebarStateChange: handleSidebarStateChange,
      linkColor: theme.palette.primary.main,
      permanentBreakpoint: "lg"
    }), /*#__PURE__*/_jsxs(IndexLayoutShell, {
      style: indexLayout.style,
      hasSideNav: showSideNav,
      accent: ACCENT,
      ariaLabel: "Regulations Card Grid",
      dataSelectTarget: "Regulations Card Grid",
      children: [/*#__PURE__*/_jsx(IndexListHeader, {
        style: indexLayout.style,
        showCreate: true,
        ariaLabel: "Regulations List Header",
        title: "Regulations",
        createLabel: "Create Regulatory Brief",
        onCreate: handleCreateClick,
        accent: ACCENT,
        description: "Browse our comprehensive collection of regulations and compliance guidelines. Use the navigation panel to explore specific categories or click on any regulation below to view detailed information."
      }), /*#__PURE__*/_jsx(FilterBar, {
        accentColor: ACCENT.primary,
        selectTarget: "Regulation Filters",
        searchSubjectLabel: "Regulations",
        filterMin: INDEX_FILTER_MIN,
        itemCount: sortedPublished.length,
        search: true,
        searchPlaceholder: "Search regulations...",
        searchValue: regSearch,
        onSearchChange: setRegSearch,
        searchScope: "regulations",
        viewMode: regViewMode,
        onViewModeChange: setRegViewMode,
        filter1: {
          name: "Category",
          pluralName: "Categories",
          options: regFilterOptions[0] ?? [],
          value: regFilterValues[0] ?? "",
          onChange: v => setRegFilterAt(0, v)
        },
        filter2: {
          name: "Region",
          pluralName: "Regions",
          options: regFilterOptions[1] ?? [],
          value: regFilterValues[1] ?? "",
          onChange: v => setRegFilterAt(1, v)
        },
        filter3: {
          name: "Type",
          pluralName: "Types",
          options: regFilterOptions[2] ?? [],
          value: regFilterValues[2] ?? "",
          onChange: v => setRegFilterAt(2, v)
        }
      }), /*#__PURE__*/_jsx(IndexResultsGrid, {
        viewMode: regViewMode,
        showRelevanceSection: showRelevanceSection,
        relevantItems: relevant,
        remainingItems: remaining,
        fallbackItems: visiblePublished,
        renderCard: renderRegulationCard,
        getKey: reg => String(reg.id),
        showSkeletons: showSkeletons,
        skeletonCount: skeletonCount,
        renderSkeleton: i => /*#__PURE__*/_jsx(RegulationCardSkeleton, {}, `skel-${i}`),
        leadingSlot: draftReg ? /*#__PURE__*/_jsx(CreateDraftCard, {
          draft: draftReg,
          untitledLabel: "Untitled Regulation",
          onOpen: handleCreateClick,
          onClear: clearDraft
        }) : null
      })]
    }), /*#__PURE__*/_jsx(SubscriptionBanner, {
      sidebarOffset: showSideNav && sidebarState.isPermanent ? sidebarState.sidebarWidth : 0
    }), /*#__PURE__*/_jsx(SectionFooter, {
      hideNewsletter: true,
      sidebarOffset: showSideNav && sidebarState.isPermanent ? sidebarState.sidebarWidth : 0
    })]
  });
};
export const getStaticProps = async () => {
  return {
    props: {
      featuredRegulations
    }
  };
};
export default RegulationsPage;